<script setup lang="ts">
import { computed, onMounted, toRefs } from 'vue';
import { useDisplay } from 'vuetify';
import { VDataTableVirtual } from 'vuetify/components';
import AddButton from './AddButton.vue';
import useInfiniteScroll from '../helpers/use-infinite-scroll';
import useLocale from '../helpers/use-locale';

type ReadonlyHeaders = VDataTableVirtual['headers'];
type HeadersProps = VDataTableVirtual['headerProps'];

type Props = {
  modelValue: string[]; // selected via checkboxes
  rounded: boolean; // adds css rounding
  contentItems: any[]; // items for datatable
  heads: ReadonlyHeaders;
  parentScrollElement: HTMLElement | null; // parent element for infinite scroll
  selectAllContent?: (isAllSelected: boolean, selectAllDefault: (value: boolean) => void) => void; // callback function to select all content in case of custom needs
  disabledToAddContent?: string[]; // content that already added in collection
  isLoading?: boolean;
  fixedHeader?: boolean;
  hasMoreContent?: boolean;
  isHistoryCollection?: boolean; //Will be cast to false if it is absent.
};

const props = defineProps<Props>();

type Emits = {
  (e: 'openContent', id: string): void;
  (e: 'addContent', content: string[]): void;
  (e: 'removeContent'): void;
  (e: 'onLoadContent'): void;
  (e: 'update:modelValue', value: string[]): void;
};

const emit = defineEmits<Emits>();

const { t, locale } = useLocale();
const { xs } = useDisplay();
const { modelValue, contentItems, parentScrollElement, disabledToAddContent, heads } = toRefs(props);

const onLoad = () => {
  emit('onLoadContent');
};

onMounted(() => {
  if (parentScrollElement?.value) {
    useInfiniteScroll(onLoad, parentScrollElement.value);
  }
});

const updateModel = (value: string[]) => {
  emit('update:modelValue', value);
};
const doSelectAll = (allSelected: boolean, defaultSelectAll: (allSelected: boolean) => void) => {
  const maxSelectedItems = 120;
  if (allSelected) {
    updateModel([]);
    return;
  }
  if (typeof props.selectAllContent === 'function') {
    props.selectAllContent(allSelected, defaultSelectAll);
    return;
  }
  if (contentItems.value.length > maxSelectedItems) {
    const selected = contentItems.value.slice(0, maxSelectedItems).map((c) => c.id);
    updateModel(selected);
    return;
  }
  defaultSelectAll(!allSelected);
};

const doToggleSort = (column: any, callback: (column: any) => void) => {
  if (!column?.sortable) {
    return;
  }
  callback(column);
};

const addContent = (contentId: string) => {
  if (contentId) {
    emit('addContent', [contentId]);
    return;
  }
  emit('addContent', modelValue.value);
  updateModel([]);
};
const isContentInAdded = (contentId: string) => {
  if (!disabledToAddContent.value || disabledToAddContent.value.length === 0) {
    return false;
  }
  return disabledToAddContent.value.includes(contentId);
};

const getCustomSort =
  () =>
  (a: any, b: any): number => {
    // Handle numeric values (treat undefined as -1 for sorting)
    if (typeof a === 'number' || typeof b === 'number') {
      const numA = a !== undefined ? a : -1;
      const numB = b !== undefined ? b : -1;
      return numA - numB;
    }
    // Use localeCompare for strings
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b, locale(), {
        sensitivity: 'base',
        numeric: true,
      });
    }

    return a > b ? 1 : a < b ? -1 : 0;
  };

const customKeySort = computed(() =>
  heads.value?.reduce(
    (acc: Record<string, any>, header: any) => ({
      ...acc,
      // @ts-ignore
      [header.key]: getCustomSort(),
    }),
    {},
  ),
);
</script>

<template>
  <div class="content-table-wrapper">
    <v-data-table-virtual
      :model-value="modelValue"
      :fixed-header="fixedHeader"
      :item-selectable="(item: any) => !isContentInAdded(item.id)"
      :headers="heads as ReadonlyHeaders"
      :items="contentItems"
      :loading="isLoading"
      class="content-table"
      :no-data-text="t('components.no_data_available')"
      :loading-text="t('components.loading_items')"
      :custom-key-sort="customKeySort"
      @update:modelValue="updateModel"
    >
      <template #[`headers`]="{ columns, isSorted, getSortIcon, toggleSort, selectAll, allSelected }: HeadersProps">
        <tr>
          <template v-for="column in columns" :key="column.key">
            <th
              v-if="column.key === 'data-table-select'"
              class="bg-transparent mr-2"
              scope="col"
              :class="`text-${column.align}`"
              :style="`min-width: ${column.width}`"
            >
              <v-btn
                v-if="!isHistoryCollection"
                class="pa-0 px-1 rounded-lg"
                :disabled="isLoading"
                variant="text"
                @click="doSelectAll(allSelected, selectAll)"
              >
                <span class="text-none select-all">
                  {{ allSelected ? t('components.deselect_all') : t('components.select_all') }}
                </span>
              </v-btn>
            </th>
            <th
              v-else
              class="bg-transparent mr-2"
              scope="col"
              :class="{
                'cursor-pointer': column.sortable,
                'text-center': column.align === 'center',
                'text-start': column.align === 'start',
              }"
              :style="`width: ${column.width}`"
              @click="doToggleSort(column, toggleSort)"
            >
              <span v-if="column?.title">{{ t(column.title) }}</span>
              <template v-if="column.sortable && isSorted(column)">
                <v-icon :icon="getSortIcon(column)"></v-icon>
              </template>
            </th>
          </template>
        </tr>
      </template>

      <template #[`item.data-table-select`]="{ item, isSelected, toggleSelect, internalItem }">
        <v-checkbox-btn v-if="!internalItem.selectable" :model-value="true" disabled />
        <v-checkbox-btn
          v-else
          :model-value="isSelected(internalItem)"
          :aria-label="t('components.select_item', { name: item.title })"
          @update:model-value="toggleSelect(internalItem)"
        />
      </template>
      <template #[`item.id`]="{ item }">
        <v-card width="55" max-height="55" class="ma-2 ml-0" @click="emit('openContent', item.id)">
          <slot :item="item" :id="item.id" :title="item.title" name="itemCover"></slot>
        </v-card>
      </template>
      <template #[`item.title`]="{ item }">
        <div class="d-flex align-center">
          <slot v-if="item.properties.isSpeakingBook" name="prepend-label"></slot>
          <v-btn class="pl-0" variant="text" flat @click="emit('openContent', item.id)">
            <span class="content-title text-none text-wrap text-left">{{ item.title }}</span>
          </v-btn>
        </div>
      </template>
      <template #[`item.addContent`]="{ item }">
        <div v-if="isContentInAdded(item.id)">
          <span class="text-on-tertiary-container text-no-wrap font-weight-bold">
            <v-icon>mdi-checkbox-marked-circle-outline</v-icon>
            <span v-if="!xs">{{ t('components.added') }}</span>
          </span>
        </div>
        <AddButton
          v-else
          :label="t('components.add_button_label', { title: item.title })"
          @click-add="addContent(item.id)"
        />
      </template>
      <template #[`item.authors`]="{ value }">
        <slot name="itemAuthors" :authors="value"></slot>
      </template>
      <template #[`item.duration`]="{ value }">
        <slot name="itemDuration" :duration="value"></slot>
      </template>
      <template #[`item.isbn`]="{ value }">
        <slot name="itemISBN" :isbn="value"></slot>
      </template>

      <template #bottom>
        <div v-if="hasMoreContent && !isLoading" class="text-center">
          <v-btn class="text-none" size="small" variant="outlined" color="primary" @click="onLoad">
            {{ t('show_button.more') }}
          </v-btn>
        </div>
        <div v-if="isLoading" class="d-flex justify-center">
          <v-progress-circular indeterminate :size="40"></v-progress-circular>
        </div>
      </template>
    </v-data-table-virtual>
  </div>
</template>

<style scoped lang="scss">
@use 'vuetify/settings' as v;

.select-btn {
  font-size: 1.1rem;
  font-weight: bold;
}

.content-table {
  background-color: transparent;
  overflow: hidden;
}

:deep(.v-table__wrapper) {
  overflow: visible;
}

:deep(.v-selection-control) {
  justify-content: center;
}

:deep(.v-data-table__td) {
  border: none !important;
}

:deep(.v-data-table__th) {
  border-bottom: 1px solid #000 !important;
}

:deep(input) {
  outline: none !important;
}

.select-all {
  text-wrap: wrap;
}

@media #{map-get(v.$display-breakpoints, 'xs')} {
  :deep(.v-data-table__td) {
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 70px;
    overflow: hidden;
  }
  .select-all {
    font-size: 11px;
  }
}
</style>
